import { escapeHtml } from './dom.js';
import { placeOptionLabel, placeOptionSubtitle, searchPlaces } from './place-search.js';
import { parseCoordsFromMapsUrl } from './map-coords.js';

export function bindPlaceAutocomplete(inputEl, options = {}) {
  if (!inputEl || inputEl.dataset.placeAutocompleteBound) return;
  inputEl.dataset.placeAutocompleteBound = '1';

  const form = options.form ?? inputEl.closest('form');
  const country = options.country ?? '';
  let debounceTimer = null;
  let activeIndex = -1;
  let results = [];

  const panel = document.createElement('div');
  panel.className = 'place-ac';
  panel.hidden = true;
  panel.setAttribute('role', 'listbox');
  inputEl.insertAdjacentElement('afterend', panel);

  function hidden(name) {
    return form?.querySelector(`[name="${name}"]`);
  }

  function setHiddenFields(place) {
    const set = (name, value) => {
      const el = hidden(name);
      if (el) el.value = value ?? '';
    };
    if (!place) {
      set('lat', '');
      set('lng', '');
      set('placeId', '');
      set('placeMeta', '');
      set('mapsUrl', '');
      updatePreview(form, null);
      return;
    }
    set('lat', String(place.lat));
    set('lng', String(place.lng));
    set('placeId', place.placeId ?? '');
    set('placeMeta', JSON.stringify(place.placeMeta ?? {}));
    const mapsEl = hidden('mapsUrl');
    if (mapsEl && place.mapsUrl) mapsEl.value = place.mapsUrl;
    options.onSelect?.(place);
    updatePreview(form, place.placeMeta);
  }

  function closePanel() {
    panel.hidden = true;
    activeIndex = -1;
    results = [];
  }

  function renderResults(list) {
    results = list;
    if (!list.length) {
      panel.hidden = true;
      return;
    }
    panel.innerHTML = list
      .map(
        (place, i) => `
          <button type="button" class="place-ac__option${i === activeIndex ? ' place-ac__option--active' : ''}" role="option" data-idx="${i}">
            <span class="place-ac__name">${escapeHtml(placeOptionLabel(place))}</span>
            <span class="place-ac__sub">${escapeHtml(placeOptionSubtitle(place))}</span>
          </button>
        `,
      )
      .join('');
    panel.hidden = false;

    panel.querySelectorAll('.place-ac__option').forEach((btn) => {
      btn.addEventListener('mousedown', (e) => {
        e.preventDefault();
        selectIndex(Number(btn.dataset.idx));
      });
    });
  }

  function selectIndex(idx) {
    const place = results[idx];
    if (!place) return;
    inputEl.value = place.name;
    setHiddenFields(place);
    closePanel();
  }

  async function runSearch() {
    const q = inputEl.value.trim();
    if (q.length < 2) {
      closePanel();
      return;
    }
    panel.innerHTML = '<p class="place-ac__loading">Cerco…</p>';
    panel.hidden = false;
    const list = await searchPlaces(q, { country, limit: 6 });
    renderResults(list);
  }

  inputEl.addEventListener('input', () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(runSearch, 400);
  });

  inputEl.addEventListener('keydown', (e) => {
    if (panel.hidden || !results.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      activeIndex = Math.min(activeIndex + 1, results.length - 1);
      renderResults(results);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      activeIndex = Math.max(activeIndex - 1, 0);
      renderResults(results);
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      selectIndex(activeIndex);
    } else if (e.key === 'Escape') {
      closePanel();
    }
  });

  inputEl.addEventListener('blur', () => {
    setTimeout(closePanel, 150);
  });

  const existingMeta = hidden('placeMeta')?.value;
  if (existingMeta) {
    try {
      updatePreview(form, JSON.parse(existingMeta));
    } catch {
      /* ignore */
    }
  }
}

function updatePreview(form, placeMeta) {
  if (!form) return;
  let preview = form.querySelector('.place-card-preview');
  if (!placeMeta?.displayName) {
    preview?.remove();
    return;
  }
  if (!preview) {
    preview = document.createElement('div');
    preview.className = 'place-card-preview';
    const anchor = form.querySelector('[data-place-preview-anchor]') ?? form.querySelector('.custom-form__actions');
    anchor?.insertAdjacentElement('beforebegin', preview);
  }
  preview.innerHTML = `
    <p class="place-card-preview__label">Luogo</p>
    <p class="place-card-preview__text">${escapeHtml(placeMeta.displayName)}</p>
    ${placeMeta.type ? `<p class="place-card-preview__type">${escapeHtml(placeMeta.type)}</p>` : ''}
  `;
}

export function placeHiddenFieldsHtml(values = {}) {
  const meta =
    typeof values.placeMeta === 'string'
      ? values.placeMeta
      : JSON.stringify(values.place_meta ?? values.placeMeta ?? {});
  return `
    <input type="hidden" name="lat" value="${escapeHtml(String(values.lat ?? ''))}" />
    <input type="hidden" name="lng" value="${escapeHtml(String(values.lng ?? ''))}" />
    <input type="hidden" name="placeId" value="${escapeHtml(values.placeId ?? values.place_id ?? '')}" />
    <input type="hidden" name="placeMeta" value="${escapeHtml(meta)}" />
  `;
}

export function parsePlaceMetaFromForm(fd) {
  const raw = String(fd.get('placeMeta') ?? '').trim();
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function parseCoordsFromForm(fd) {
  const mapsUrl = String(fd.get('mapsUrl') || '').trim();
  const fromUrl = parseCoordsFromMapsUrl(mapsUrl);
  if (fromUrl) return fromUrl;

  const lat = Number(fd.get('lat') || fd.get('latVisible'));
  const lng = Number(fd.get('lng') || fd.get('lngVisible'));
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return { lat: null, lng: null };
  return { lat, lng };
}
